import _jsx from '@babel/runtime/helpers/jsx';
import React, { useMemo, useState, useEffect, forwardRef } from 'react';
import classNames from 'classnames';
import { VisuallyHidden } from '@andes/common';
import { getPatchedI18n, AndesContext } from '@andes/context';
import { ProgressIndicatorCircular } from '@andes/progress-indicator-circular';
import '@andes/common/build/utils/propTypesValidators';
import { Button } from '@andes/button';
import Add16 from '@andes/icons/Add16';
import Add20 from '@andes/icons/Add20';
import Substract16 from '@andes/icons/Substract16';
import Substract20 from '@andes/icons/Substract20';
import FeedbackNegative12 from '@andes/icons/FeedbackNegative12';
const namespace = 'andes-input-stepper';
const buttonNamespace = "".concat(namespace, "__button");
const SIZE_SMALL = 'small';
const PROGRESS_INDICATOR_SIZE_MEDIUM = 'medium';
const PROGRESS_INDICATOR_SIZE_SMALL = 'small';
const BUTTON_TYPE_DECREMENT = 'decrement';
const BUTTON_TYPE_INCREMENT = 'increment';
const BUTTON_DEFAULT_LABELS_BY_TYPE = {
  [BUTTON_TYPE_DECREMENT]: 'quitar',
  [BUTTON_TYPE_INCREMENT]: 'agregar'
};
const InputStepperButton = /*#__PURE__*/function () {
  const InputStepperButton = _ref => {
    let {
      'aria-controls': ariaControls,
      'aria-describedby': ariaDescribedby,
      'aria-labelledby': ariaLabelledby,
      buttonLabelId,
      disabled,
      i18n,
      id,
      onClick,
      size,
      srLabel,
      type
    } = _ref;
    const Icon = useMemo(() => {
      if (size === SIZE_SMALL) {
        return type === BUTTON_TYPE_DECREMENT ? Substract16 : Add16;
      }
      return type === BUTTON_TYPE_DECREMENT ? Substract20 : Add20;
    }, [size, type]);
    const handleClick = event => {
      if (!disabled && typeof onClick === 'function') onClick(event);
    };
    const buttonClassnames = classNames(buttonNamespace, "".concat(buttonNamespace, "--").concat(type), "".concat(buttonNamespace, "--").concat(size));
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/_jsx(VisuallyHidden, {
      "aria-hidden": true
    }, void 0, /*#__PURE__*/_jsx("span", {
      id: buttonLabelId
    }, void 0, srLabel || i18n.pgettext(namespace, BUTTON_DEFAULT_LABELS_BY_TYPE[type]))), /*#__PURE__*/_jsx(Button, {
      "aria-controls": ariaControls,
      "aria-describedby": ariaDescribedby,
      "aria-labelledby": ariaLabelledby,
      className: buttonClassnames,
      disabled: disabled,
      hierarchy: "transparent",
      onClick: handleClick,
      id: id
    }, void 0, /*#__PURE__*/_jsx(Icon, {})));
  };
  InputStepperButton.displayName = 'InputStepperButton';
  return InputStepperButton;
}();
var InputStepperButton$1 = InputStepperButton;
const useLoadedOnce = loading => {
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  useEffect(() => {
    if (loading) {
      setHasLoadedOnce(true);
    }
  }, [loading]);
  return hasLoadedOnce;
};
var useLoadedOnce$1 = useLoadedOnce;
const MODIFIER_ERROR = 'error';
const MODIFIER_READONLY = 'readonly';
const DEFAULT_STEP = 1;
var module$2 = "@andes/input-stepper";
var locale$2 = "en-US";
var translations$2 = {
  "andes-input-stepper\u0004quitar": [null, "Remove"],
  "andes-input-stepper\u0004agregar": [null, "Add"]
};
var enTranslations = {
  module: module$2,
  locale: locale$2,
  translations: translations$2
};
var module$1 = "@andes/input-stepper";
var locale$1 = "es-AR";
var translations$1 = {
  "andes-input-stepper\u0004quitar": [null, "Quitar"],
  "andes-input-stepper\u0004agregar": [null, "Agregar"]
};
var esTranslations = {
  module: module$1,
  locale: locale$1,
  translations: translations$1
};
var module = "@andes/input-stepper";
var locale = "pt-BR";
var translations = {
  "andes-input-stepper\u0004quitar": [null, "Excluir"],
  "andes-input-stepper\u0004agregar": [null, "Adicionar"]
};
var ptTranslations = {
  module: module,
  locale: locale,
  translations: translations
};
var _FeedbackNegative;
const InputStepper = /*#__PURE__*/function () {
  const InputStepper = /*#__PURE__*/forwardRef((_ref, ref) => {
    let {
      className,
      complementaryText,
      decrementButtonProps = {},
      defaultValue,
      disabled = false,
      formatValue = value => value,
      helper,
      id,
      incrementButtonProps = {},
      label,
      loading = false,
      maxValue,
      minValue = 0,
      modifier,
      onChange,
      size = 'large',
      srLabel,
      step = 1,
      value
    } = _ref;
    const isControlled = useMemo(() => typeof value !== 'undefined', []);
    const i18n = getPatchedI18n(AndesContext, enTranslations, esTranslations, ptTranslations);
    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
    const hasLoadedOnce = useLoadedOnce$1(loading);
    const isSmall = size === SIZE_SMALL;
    const isReadOnly = modifier === MODIFIER_READONLY;
    const showLabel = !isSmall && label;
    const showButtons = !isReadOnly;
    const showHelper = !isSmall && !disabled && !isReadOnly && helper;
    const getValue = () => {
      if (isControlled) return value;
      return uncontrolledValue || 0;
    };
    const getStep = () => {
      if (step && step > 0) return step;
      return DEFAULT_STEP;
    };
    const stepMaxOutOfBounds = maxValue ? getValue() + getStep() > maxValue : false;
    const stepMinOutOfBounds = getValue() - getStep() < minValue;
    const handleDecrement = event => {
      const {
        onClick
      } = decrementButtonProps;
      const newValue = getValue() - getStep();
      if (!isControlled) setUncontrolledValue(newValue);
      if (typeof onChange === 'function') onChange(newValue);
      if (typeof onClick === 'function') onClick(event);
    };
    const handleIncrement = event => {
      const {
        onClick
      } = incrementButtonProps;
      const newValue = getValue() + getStep();
      if (!isControlled) setUncontrolledValue(newValue);
      if (typeof onChange === 'function') onChange(newValue);
      if (typeof onClick === 'function') onClick(event);
    };
    const componentClassnames = classNames(namespace, className, {
      ["".concat(namespace, "--disabled")]: disabled,
      ["".concat(namespace, "--").concat(modifier)]: modifier && !disabled
    });
    const labelClassnames = classNames("".concat(namespace, "__label"), {
      ["".concat(namespace, "__label--with-spacing")]: label
    });
    const wrapperClassnames = classNames("".concat(namespace, "__wrapper"), {
      ["".concat(namespace, "__wrapper--").concat(size)]: size,
      ["".concat(namespace, "__wrapper--fade")]: loading
    });
    const containerClassnames = classNames("".concat(namespace, "__container"), {
      ["".concat(namespace, "__container--animate")]: hasLoadedOnce
    });
    const contentClassnames = classNames("".concat(namespace, "__content"), {
      ["".concat(namespace, "__content--with-complementary-text")]: complementaryText
    });
    const getHelper = () => {
      if (showHelper) {
        return /*#__PURE__*/_jsx("span", {
          className: "".concat(namespace, "__helper"),
          id: "".concat(id, "-helper")
        }, void 0, MODIFIER_ERROR === modifier && /*#__PURE__*/_jsx("span", {
          className: "".concat(namespace, "__error-icon"),
          "aria-hidden": true
        }, void 0, _FeedbackNegative || (_FeedbackNegative = /*#__PURE__*/_jsx(FeedbackNegative12, {
          color: "white"
        }))), helper);
      }
      return null;
    };
    const getButtonProps = type => {
      const isDecrementButton = type === BUTTON_TYPE_DECREMENT;
      const buttonProps = {
        'aria-controls': "".concat(id, "-content"),
        'aria-describedby': showHelper ? "".concat(id, "-helper") : undefined,
        'aria-labelledby': "".concat(id, "-label ").concat(id, "-").concat(type, "-text"),
        buttonLabelId: "".concat(id, "-").concat(type, "-text"),
        disabled: false,
        i18n,
        id: "".concat(id, "-").concat(isDecrementButton ? 'decrement' : 'increment'),
        onClick: isDecrementButton ? handleDecrement : handleIncrement,
        size,
        srLabel: (isDecrementButton ? decrementButtonProps : incrementButtonProps).srLabel,
        type
      };
      if (isDecrementButton) {
        buttonProps.disabled = disabled || getValue() <= minValue || stepMinOutOfBounds;
      } else {
        buttonProps.disabled = disabled || (maxValue ? getValue() >= maxValue : false) || stepMaxOutOfBounds;
      }
      return buttonProps;
    };
    const LabelContainer = showLabel ? 'span' : VisuallyHidden;
    return /*#__PURE__*/React.createElement("div", {
      className: componentClassnames,
      id: id,
      ref: ref
    }, /*#__PURE__*/_jsx("div", {
      className: labelClassnames,
      id: "".concat(id, "-label")
    }, void 0, label && /*#__PURE__*/_jsx(LabelContainer, {
      "aria-hidden": srLabel ? true : undefined
    }, void 0, label), srLabel && /*#__PURE__*/_jsx(VisuallyHidden, {}, void 0, srLabel)), /*#__PURE__*/_jsx("div", {
      className: wrapperClassnames
    }, void 0, /*#__PURE__*/_jsx("div", {
      className: containerClassnames
    }, void 0, showButtons && /*#__PURE__*/React.createElement(InputStepperButton$1, getButtonProps(BUTTON_TYPE_DECREMENT)), /*#__PURE__*/_jsx("div", {
      "aria-atomic": "true",
      "aria-live": "assertive",
      className: contentClassnames,
      id: "".concat(id, "-content"),
      role: "alert"
    }, void 0, /*#__PURE__*/_jsx("span", {
      className: "".concat(namespace, "__value")
    }, void 0, formatValue(getValue()), complementaryText && /*#__PURE__*/_jsx("span", {
      className: "".concat(namespace, "__complementary-text")
    }, void 0, ' ', complementaryText))), showButtons && /*#__PURE__*/React.createElement(InputStepperButton$1, getButtonProps(BUTTON_TYPE_INCREMENT))), (loading || hasLoadedOnce) && /*#__PURE__*/_jsx("div", {
      className: "".concat(namespace, "__progress-indicator-container")
    }, void 0, /*#__PURE__*/_jsx(ProgressIndicatorCircular, {
      className: "".concat(namespace, "__progress-indicator"),
      size: isSmall ? PROGRESS_INDICATOR_SIZE_SMALL : PROGRESS_INDICATOR_SIZE_MEDIUM
    }))), getHelper());
  });
  InputStepper.displayName = 'InputStepper';
  return InputStepper;
}();
var InputStepper$1 = InputStepper;
export { InputStepper$1 as InputStepper, InputStepper$1 as default };
